import { useState, useEffect } from "react";

import {
  Package,
  Search,
  Eye,
  Plus,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import type { Shipment } from "@/lib/supabase";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { shipmentService } from "@/lib/database";

export default function AdminShipments() {
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalShipments, setTotalShipments] = useState(0);

  const itemsPerPage = 20;
  const navigate = useNavigate();

  useEffect(() => {
    loadShipments();
  }, [currentPage, statusFilter]);

  const loadShipments = async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * itemsPerPage;
      const { shipments: data, total } = await shipmentService.getAll(
        itemsPerPage,
        offset
      );

      let filteredData = data;
      if (statusFilter !== "all") {
        filteredData = data.filter((s) => s.status === statusFilter);
      }

      setShipments(filteredData);
      setTotalShipments(total);
      setTotalPages(Math.ceil(total / itemsPerPage));
    } catch (error) {
      console.error("Error loading shipments:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      picked_up_by_recipient:
        "bg-green-100 text-green-800 border-green-200 hover:bg-green-200",
      delivered_to_recipient:
        "bg-green-100 text-green-800 border-green-200 hover:bg-green-200",
      in_transit_to_country:
        "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200",
      in_transit_to_recipient:
        "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200",
      ready_for_pickup:
        "bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-200",
      arrived_at_warehouse:
        "bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-200",
      processing_at_warehouse:
        "bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200",
      picking_up_from_sender:
        "bg-teal-100 text-teal-800 border-teal-200 hover:bg-teal-200",
      received_at_warehouse:
        "bg-sky-100 text-sky-800 border-sky-200 hover:bg-sky-200",
      created: "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200",
      estimated:
        "bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200",
      confirmed:
        "bg-green-100 text-green-800 border-green-200 hover:bg-green-200",
      cancelled: "bg-red-100 text-red-800 border-red-200 hover:bg-red-200",
      refunded: "bg-red-100 text-red-800 border-red-200 hover:bg-red-200",
    };

    return (
      <Badge
        className={
          variants[status as keyof typeof variants] ||
          "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200"
        }
      >
        {status.replace(new RegExp("_", "g"), " ").toUpperCase()}
      </Badge>
    );
  };

  const filteredShipments = shipments.filter((shipment) => {
    const matchesSearch =
      shipment.tracking_number
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      shipment.customer?.first_name
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      shipment.customer?.last_name
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between space-x-2">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            All Shipments
          </h1>
          <p className="text-gray-600">
            Manage and track all shipments in the system
          </p>
        </div>

        <Link to="/admin/shipments/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            New Shipment
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between space-x-2">
            <div>
              <CardTitle className="text-xl md:text-2xl">
                Shipments ({totalShipments})
              </CardTitle>
              <CardDescription>
                All shipments throughout the platform
              </CardDescription>
            </div>

            <div className="flex items-center space-x-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search shipments..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {/* <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="received">Received</SelectItem>
                    <SelectItem value="processing">Processing</SelectItem>
                    <SelectItem value="in_transit">In Transit</SelectItem>
                    <SelectItem value="arrived">Arrived</SelectItem>
                    <SelectItem value="ready_pickup">Ready Pickup</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                  </SelectContent>
                </Select> */}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredShipments.map((shipment) => (
              <div
                key={shipment.id}
                className="flex flex-col md:flex-row md:items-center md:justify-between p-4 border border-gray-200 rounded-lg bg-gray-50 hover:bg-white shadow-sm transition-colors"
              >
                <div className="flex items-center space-x-4">
                  <Package className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-900">
                      {shipment.tracking_number}
                    </p>

                    <p className="text-sm text-gray-600">
                      {shipment.customer?.first_name}{" "}
                      {shipment.customer?.last_name}
                    </p>

                    <p className="text-sm text-gray-600">
                      {shipment.route === "us_to_ht"
                        ? "USA to Haiti"
                        : "Haiti to USA"}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col mt-4 md:mt-0 md:flex-row md:items-center md:space-x-6">
                  <div className="flex justify-between space-x-6">
                    <div className="md:text-center">
                      <p className="text-sm font-medium text-gray-900">
                        Method
                      </p>
                      <p className="text-xs text-gray-600 capitalize">
                        {shipment.shipping_method}
                      </p>
                    </div>

                    {shipment.shipping_method === "air" && (
                      <div className="text-right md:text-center">
                        <p className="text-sm font-medium text-gray-900">
                          Total Weight
                        </p>
                        <p className="text-xs text-gray-600">
                          {shipment.total_weight} lbs
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex mt-2 md:mt-0 justify-between space-x-6">
                    <div className="md:text-center">
                      <p className="text-sm font-medium text-gray-900">Cost</p>
                      <p className="text-xs text-gray-600">
                        ${shipment.total_cost}
                      </p>
                    </div>

                    <div className="text-right md:text-center">
                      <p className="text-sm font-medium text-gray-900">
                        Created
                      </p>
                      <p className="text-xs text-gray-600">
                        {new Date(shipment.created_at!).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-center my-4 md:my-0">
                    <div className="flex items-center space-x-2">
                      {getStatusBadge(shipment.status!)}
                    </div>
                  </div>

                  <Button
                    size="sm"
                    onClick={() => {
                      navigate(`/admin/shipments/${shipment.tracking_number}`);
                    }}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </Button>
                  {/* <Button variant="ghost" size="sm">
                        <Edit 
                          className="h-4 w-4" 
                          onClick={() => {
                            setSelectedShipment(shipment);
                            setIsShipmentModalOpen(true);
                          }}
                        />
                      </Button> */}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-6 space-x-2">
            <p className="text-sm text-gray-600">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, totalShipments)} of{" "}
              {totalShipments} shipments
            </p>

            <div className="flex relative items-center justify-center pt-6 md:pt-0 space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>

              <span className="text-sm absolute md:relative top-0 text-gray-600">
                Page {currentPage} of {totalPages}
              </span>

              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
// Utility function to calculate cubic feet from dimensions string
function calculateCubicFeet(dimensions?: string): string {
  if (!dimensions) return "0";

  const parts = dimensions.split("x").map((d) => parseFloat(d.trim()));
  if (parts.length !== 3 || parts.some(isNaN)) return "0";

  const [length, width, height] = parts;
  const cubicFeet = (length * width * height) / 1728; // Convert cubic inches to cubic feet
  return cubicFeet.toFixed(2);
}
